import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ServicemeshNavigationService } from '@helpers/components/navigation/navigation.service';
import { ServicemeshNavigationItem } from '@helpers/components/navigation/navigation.types';
import { ServicemeshVerticalNavigationBasicItemComponent } from '@helpers/components/navigation/vertical/components/basic/basic.component';
import { ServicemeshVerticalNavigationCollapsableItemComponent } from '@helpers/components/navigation/vertical/components/collapsable/collapsable.component';
import { ServicemeshVerticalNavigationDividerItemComponent } from '@helpers/components/navigation/vertical/components/divider/divider.component';
import { ServicemeshVerticalNavigationSpacerItemComponent } from '@helpers/components/navigation/vertical/components/spacer/spacer.component';
import { ServicemeshVerticalNavigationComponent } from '@helpers/components/navigation/vertical/vertical.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'servicemesh-vertical-navigation-group-item',
  templateUrl: './group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    MatIconModule,
    ServicemeshVerticalNavigationBasicItemComponent,
    forwardRef(() => ServicemeshVerticalNavigationCollapsableItemComponent),
    ServicemeshVerticalNavigationDividerItemComponent,
    forwardRef(() => ServicemeshVerticalNavigationGroupItemComponent),
    ServicemeshVerticalNavigationSpacerItemComponent,
  ],
})
export class ServicemeshVerticalNavigationGroupItemComponent implements OnInit, OnDestroy {
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _servicemeshNavigationService = inject(ServicemeshNavigationService);

  /* eslint-disable @typescript-eslint/naming-convention */
  static ngAcceptInputType_autoCollapse: BooleanInput; // NOSONAR
  /* eslint-enable @typescript-eslint/naming-convention */

  @Input() autoCollapse!: boolean;
  @Input() item!: ServicemeshNavigationItem;
  @Input() name!: string;

  private _servicemeshVerticalNavigationComponent!: ServicemeshVerticalNavigationComponent;
  private readonly _unsubscribeAll: Subject<unknown> = new Subject<unknown>();

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Get the parent navigation component
    this._servicemeshVerticalNavigationComponent = this._servicemeshNavigationService.getComponent(
      this.name
    );

    // Subscribe to onRefreshed on the navigation component
    this._servicemeshVerticalNavigationComponent.onRefreshed
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        // Mark for check
        this._changeDetectorRef.markForCheck();
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: ServicemeshNavigationItem): string | number {
    return item.id || index;
  }
}
